import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { FinancialConvoContext } from "FinancialConvoContextWrapper";
import { FinancialConvo } from "models/FinancialConvo";
import { useAuthContext } from "../../../AuthContextWrapper";
import { OUTCOMES } from "../../../constants";
import {
  MultiSelectButtonForm,
  SelectButton,
} from "../shared/MultiSelectButtonForm";
import {
  AccountsCollectionsIcon,
  AssetsMultipleStatesIcon,
  BusinessesIcon,
  InsuranceAccountsIcon,
  InvestmentBrokerageIcon,
  JeweleryIcon,
  NoneOfTheseAssetsIcon,
  RentalRealEstateIcon,
  RetirementAccountIcon,
  TrustIcon,
  VehiclesIcon,
} from "../shared/LineIcons";
import { enumToStringDictionary } from "@theatticusapp/atticus-shared-components";

export enum Outcomes {
  rentalRE = "Rental real estate property (non-primary residence)",
  investment = "Investment / brokerage accounts",
  retirement = "Retirement accounts",
  business = "Own one or more businesses",
  multiStateAssets = "Assets in multiple states",
  // highValueEstate = "Total estate assets value over {$XYZk}",
  jewelry = "Jewelry",
  insuranceAccounts = "Insurance accounts",
  vehicles = "Vehicles (e.g. boat, plane, car, tractor)",
  collections = "Accounts in collections",
  trust = "There is a trust",
  none = "None of these assets",
}

const iconStyles = { width: 60, height: 60 } as const;
const iconViewBox = "0 0 60 60";

const icons: { [key: string]: JSX.Element } = {
  rentalRE: <RentalRealEstateIcon sx={iconStyles} viewBox={iconViewBox} />,
  investment: <InvestmentBrokerageIcon sx={iconStyles} viewBox={iconViewBox} />,
  retirement: <RetirementAccountIcon sx={iconStyles} viewBox={iconViewBox} />,
  business: <BusinessesIcon sx={iconStyles} viewBox={iconViewBox} />,
  multiStateAssets: (
    <AssetsMultipleStatesIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  // highValueEstate: <InvestmentBrokerageIcon sx={iconStyles} viewBox={iconViewBox} />,
  jewelry: <JeweleryIcon sx={iconStyles} viewBox={iconViewBox} />,
  insuranceAccounts: (
    <InsuranceAccountsIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  vehicles: <VehiclesIcon sx={iconStyles} viewBox={iconViewBox} />,
  collections: (
    <AccountsCollectionsIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  trust: <TrustIcon sx={iconStyles} viewBox={iconViewBox} />,
  none: <NoneOfTheseAssetsIcon sx={iconStyles} viewBox={iconViewBox} />,
};

const outcomesDictionary = enumToStringDictionary(Outcomes);
const assetSelectButtons: SelectButton[] = [];
Object.keys(outcomesDictionary).forEach((key: string) =>
  assetSelectButtons.push({
    key,
    text: outcomesDictionary[key],
    icon: icons[key],
  })
);

export const UpdateAssetsScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, user } = useAuthContext()
  const { latestFinancialConvo, setLatestFinancialConvo } = useContext(
    FinancialConvoContext
  );
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (checked: string[]): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    if (!activeEstate?.id || !user?.id) {
      throw "No estate or user ID";
    }

    // const estate = activeEstate ? Estate.from(activeEstate) : new Estate();
    const financialConvo = latestFinancialConvo || new FinancialConvo();
    financialConvo.declaredTypeOfAssets = checked;

    try {
      await financialConvo.saveRequest(activeEstate?.id, user?.id);
      setLatestFinancialConvo && setLatestFinancialConvo(financialConvo);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper
      title="Have you located any new assets in the estate?"
      subTitle="Select any of the following that apply to the estate."
    >
      <MultiSelectButtonForm
        initChecked={
          latestFinancialConvo?.declaredTypeOfAssets ||
          activeEstate?.typeOfAssets ||
          undefined
        }
        selectButtons={assetSelectButtons}
        isOneRequired={true}
        noneOfAboveButtonKey="none"
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
